'use client';

import { useCallback, useEffect, useState } from 'react';
import { Marker, Popup, useMap } from 'react-map-gl';

interface MarkerWithPopupProps {
  lat: number;
  lng: number;
  marker: React.ReactNode;
  popup?: React.ReactNode;
}

let activeDismiss: (() => void) | null = null;

function dismissActivePopup() {
  activeDismiss?.();
  activeDismiss = null;
}

function openMarkerPopup(dismiss: () => void) {
  if (activeDismiss !== dismiss) {
    activeDismiss?.();
  }
  activeDismiss = dismiss;
}

function closeMarkerPopup(dismiss: () => void) {
  if (activeDismiss === dismiss) {
    dismissActivePopup();
  }
}

/**
 * Map marker with an optional popup.
 */
export function MarkerWithPopup({
  lat,
  lng,
  marker,
  popup,
}: MarkerWithPopupProps) {
  const [showPopup, setShowPopup] = useState(false);
  const { current: mapRef } = useMap();
  const dismiss = useCallback(() => setShowPopup(false), []);

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    map.on('click', dismissActivePopup);
    return () => {
      map.off('click', dismissActivePopup);
      closeMarkerPopup(dismiss);
    };
  }, [mapRef, dismiss]);

  return (
    <>
      <Marker
        longitude={lng}
        latitude={lat}
        anchor="center"
        onClick={e => {
          e.originalEvent.stopPropagation();
          if (!!popup) {
            openMarkerPopup(dismiss);
            setShowPopup(true);
          }
        }}>
        {marker}
      </Marker>
      {showPopup ? (
        <Popup
          anchor="top"
          longitude={lng}
          latitude={lat}
          closeButton={false}
          onClose={() => closeMarkerPopup(dismiss)}>
          {popup}
        </Popup>
      ) : null}
    </>
  );
}
